import { useState } from "react";
import PropTypes from "prop-types";
import { Button, FormControl, TextField } from "@material-ui/core";
import { useSnackbar } from "notistack";
import { useMutation } from "react-query";
import { postData } from "../../api/api";
import Error from "../Common/Error";
import LoadingButton from "../Common/LoadingButton";
import PrivateTextInput from "../Common/PrivateTextInput";
import { checkPasswordInvalidity, getQueryConfiguration } from "../../utils";
import { TDialog, TDialogTitle, TDialogContent, TDialogActions } from "../Common/TDialog";

export const PasswordResetDialogPresenter = (props) => {
    const [newPassword, setNewPassword] = useState();
    const [passwordConfirmation, setPasswordConfirmation] = useState();
    const [token, setToken] = useState(props.token);

    const checkValidity = () => !checkPasswordInvalidity(newPassword) && newPassword === passwordConfirmation && token;

    return (
        <TDialog open={props.open}>
            <TDialogTitle>Reset password</TDialogTitle>

            <TDialogContent>
                {props.error && (
                    <FormControl>
                        <Error message={props.error} />
                    </FormControl>
                )}

                <PrivateTextInput
                    label="New password"
                    value={newPassword ?? ""}
                    required
                    fullWidth
                    helperText={checkPasswordInvalidity(newPassword) ?? "Password is valid"}
                    onChange={(e) => {
                        setNewPassword(e.target.value);
                    }}
                    style={{ marginBottom: "5px" }}
                />

                <PrivateTextInput
                    label="Confirm password"
                    value={passwordConfirmation ?? ""}
                    required
                    fullWidth
                    helperText={
                        newPassword && newPassword === passwordConfirmation
                            ? "Password confirmed"
                            : "Password not confirmed"
                    }
                    onChange={(e) => {
                        setPasswordConfirmation(e.target.value);
                    }}
                    style={{ marginBottom: "5px" }}
                />

                <TextField
                    label="Token"
                    value={token ?? ""}
                    required
                    fullWidth
                    helperText="Password reset token sent via email"
                    onChange={(e) => {
                        setToken(e.target.value);
                    }}
                />
            </TDialogContent>

            <TDialogActions>
                <Button color="primary" disabled={props.saving} onClick={props.onCancel}>
                    Cancel
                </Button>
                <LoadingButton
                    color="primary"
                    disabled={!checkValidity()}
                    loading={props.saving}
                    onClick={() => {
                        props.onSave?.({
                            newPassword,
                            token,
                        });
                    }}
                >
                    Reset password
                </LoadingButton>
            </TDialogActions>
        </TDialog>
    );
};

PasswordResetDialogPresenter.propTypes = {
    open: PropTypes.bool.isRequired,
    token: PropTypes.string,
    saving: PropTypes.bool,
    error: PropTypes.string,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
};

PasswordResetDialogPresenter.defaultProps = {
    saving: false,
};

const PasswordResetDialog = (props) => {
    const { enqueueSnackbar } = useSnackbar();

    const mutation = useMutation((resource) => postData("/accounts/reset-password", resource), {
        onSuccess: () => {
            props.onClose?.();

            enqueueSnackbar("Password has been reset", {
                variant: "info",
            });
        },
    });

    const { applicationId, token } = getQueryConfiguration();

    const handleSave = ({ newPassword, token }) => {
        mutation.mutate({
            applicationId,
            password: newPassword,
            confirmPassword: newPassword,
            token,
        });
    };

    return (
        <PasswordResetDialogPresenter
            {...props}
            token={token}
            saving={mutation.isLoading}
            error={mutation.error?.response.data.message}
            onSave={handleSave}
            onCancel={props.onClose}
        />
    );
};

PasswordResetDialog.propTypes = {
    onClose: PropTypes.func,
};

export default PasswordResetDialog;
